"use client"
import { useState, useEffect } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import Layout from "../../components/Layout"
import {Swiper, SwiperSlide} from "swiper/react";
import {Navigation} from "swiper/modules";
import currencyConfig from '../../utils/currencyConfig';
import axios from 'axios';
import { toast } from "react-toastify";

const HomaRequestPage = () => {
    const [isMobile, setIsMobile] = useState(false)
    const [isTablet, setIsTablet] = useState(false)
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
    const navigate = useNavigate()
    const location = useLocation()
    const [showPrayerModal, setShowPrayerModal] = useState(false)
    const [slides, setSlides] = useState([])
    const [pageTitle, setPageTitle] = useState("Requests")
    const [isLoadingSlides, setIsLoadingSlides] = useState(true)
    
    // Get request type from URL path
    const requestType = location.pathname.substring(1) // Remove leading slash
    
    // Smart API URL detection
    const getApiUrl = () => {
        if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
        const hostname = window.location.hostname;
        if (hostname === '18.133.181.177' || hostname.includes('18.133.181.177')) {
            return 'http://18.133.181.177:3001/api';
        }
        return 'http://localhost:3001/api';
    };
    const API_URL = getApiUrl();


    // Dashboard cards data

    useEffect(() => {
        const checkScreenSize = () => {
            const width = window.innerWidth
            setIsMobile(width <= 768)
            setIsTablet(width > 768 && width <= 1024)
        }

        checkScreenSize()
        window.addEventListener("resize", checkScreenSize)

        return () => {
            window.removeEventListener("resize", checkScreenSize)
        }
    }, [navigate])

    // Fetch slides dynamically based on request type
    useEffect(() => {
        const fetchSlides = async () => {
            if (!requestType) return;
            
            try {
                setIsLoadingSlides(true)
                const response = await axios.get(`${API_URL}/forms/slides/${requestType}`)
                
                if (response.data.success) {
                    setSlides(response.data.data.slides)
                    setPageTitle(response.data.data.title)
                }
            } catch (error) {
                console.error('Error fetching slides:', error)
                setSlides([])
                setPageTitle("Requests")
                toast.error('Error loading request forms. Please try again later.')
            } finally {
                setIsLoadingSlides(false)
            }
        }
        
        fetchSlides()
    }, [requestType, API_URL])

    const breadcrumb = (
        <div style={{ display: "flex", alignItems: "center", gap: "8px", color: "#6b7280", fontSize: "0.9rem" }}>
            <svg 
                width="20" 
                height="20" 
                viewBox="0 0 24 24" 
                fill="currentColor"
                style={{ cursor: "pointer" }}
                onClick={() => navigate("/home")}
            >
                <path d="M10,20V14H14V20H19V12H22L12,3L2,12H5V20H10Z" />
            </svg>
            <span>/</span>
            <span style={{ cursor: "pointer" }} onClick={() => navigate("/home")}>Home</span>
            <span>/</span>
            <span>{pageTitle}</span>
        </div>
    )

    const getSidebarWidth = () => {
        if (isMobile) return "80%"
        return sidebarCollapsed ? "80px" : "250px"
    }

    const getMainContentMargin = () => {
        if (isMobile) return "0"
        return sidebarCollapsed ? "80px" : "250px"
    }

    const getMainContentWidth = () => {
        if (isMobile) return "100%"
        return sidebarCollapsed ? "calc(100% - 80px)" : "calc(100% - 250px)"
    }

    const styles = {
        mainContent: {
            marginLeft: getMainContentMargin(),
            paddingTop: "80px",
            flex: 1,
            padding: isMobile ? "20px" : "30px",
            width: getMainContentWidth(),
            transition: "margin-left 0.3s ease, width 0.3s ease",
        },
        breadcrumb: {
            display: "flex",
            alignItems: "center",
            marginBottom: "30px",
            color: "#6b7280",
            fontSize: "0.9rem",
            gap: "8px",
        },
        cardsGrid: {
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
            gap: "20px",
            width: "100%",
            marginTop: "30px",
        },
        card: {
            backgroundColor: "#7fb3a3",
            borderRadius: "12px",
            padding: "10px",
            textAlign: "center",
            color: "white",
            cursor: "pointer",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "200px",
            width: "90%",
            margin: "0 auto",
            marginTop: "20px",
            transition: "all 0.3s ease",
        },
        cardIcon: {
            fontSize: "3rem",
            marginBottom: "20px",
            opacity: 0.9,
            display: "flex",
            justifyContent: "center",
        },
        cardTitle: {
            fontSize: "1.3rem",
            fontWeight: "600",
            marginBottom: "15px",
            lineHeight: "1.3",
        },
        cardDescription: {
            fontSize: "0.95rem",
            opacity: 0.9,
            lineHeight: "1.4",
        },
        error: {
            color: "#ef4444",
            fontSize: "0.9rem",
            textAlign: "center",
            marginBottom: "20px",
        },
        swiperContainer: {
            position: "relative",
            maxWidth: "800px",
            margin: "0 auto",
            padding: "0 20px",
        },
        slideContainer: {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "40px 20px",
            minHeight: "500px",
        },
        regularCard: {
            backgroundColor: "white",
            border: "3px solid #7fb3a3",
            borderRadius: "25px",
            padding: "30px",
            maxWidth: "700px",
            width: "100%",
            height: "400px",
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            alignItems: "center",
            gap: "30px",
            boxShadow: "0 8px 25px rgba(0,0,0,0.1)",
        },
        specialCard: {
            backgroundColor: "white",
            border: "3px solid #7fb3a3",
            borderRadius: "25px",
            padding: "30px",
            maxWidth: "700px",
            width: "100%",
            height: "400px",
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            alignItems: "center",
            gap: "30px",
            boxShadow: "0 8px 25px rgba(0,0,0,0.1)",
        },
        formPreviewSection: {
            flex: "1",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            maxWidth: isMobile ? "100%" : "300px",
            height: isMobile ? "200px" : "100%",
        },
        formPreviewImg: {
            width: "100%",
            height: "100%",
            objectFit: "cover",
            borderRadius: "15px",
            border: "2px solid #e5e7eb",
            maxWidth: "280px",
            maxHeight: "340px",
        },
        formPreviewImgSpecial: {
            width: "100%",
            height: "100%",
            objectFit: "cover",
            borderRadius: "15px",
            border: "2px solid #e5e7eb",
            maxWidth: "280px",
            maxHeight: "340px",
        },
        cardContent: {
            flex: "1",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: isMobile ? "center" : "flex-start",
            textAlign: isMobile ? "center" : "left",
            height: "100%",
            padding: "20px 0",
        },
        specialCardContent: {
            flex: "1",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: isMobile ? "center" : "flex-start",
            textAlign: isMobile ? "center" : "left",
            height: "100%",
            padding: "20px 0",
        },
        specialCardTitle: {
            fontSize: "28px",
            fontWeight: "600",
            color: "#000000",
            margin: "0 0 20px 0",
        },
        cardPrice: {
            fontSize: "16px",
            color: "#6b7280",
            fontWeight: "500",
            margin: "0 0 25px 0",
        },
        specialCardPrice: {
            fontSize: "16px",
            color: "#6b7280",
            fontWeight: "500",
            margin: "0 0 25px 0",
        },
        fillBtn: {
            backgroundColor: "white",
            color: "#000000",
            border: "2px solid #000000",
            padding: "15px 30px",
            borderRadius: "10px",
            fontSize: "16px",
            fontWeight: "600",
            cursor: "pointer",
            transition: "all 0.3s ease",
        },
        specialFillBtn: {
            backgroundColor: "white",
            color: "#000000",
            border: "2px solid #000000",
            padding: "15px 30px",
            borderRadius: "10px",
            fontSize: "16px",
            fontWeight: "600",
            cursor: "pointer",
            transition: "all 0.3s ease",
        },
        navButtonPrev: {
            position: "absolute",
            left: isMobile ? "10px" : "20px",
            top: "50%",
            transform: "translateY(-50%)",
            backgroundColor: "#7fb3a3",
            color: "white",
            border: "none",
            borderRadius: "50%",
            width: isMobile ? "45px" : "60px",
            height: isMobile ? "45px" : "60px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
            transition: "all 0.3s ease",
            zIndex: 10,
        },
        navButtonNext: {
            position: "absolute",
            right: isMobile ? "10px" : "20px",
            top: "50%",
            transform: "translateY(-50%)",
            backgroundColor: "#7fb3a3",
            color: "white",
            border: "none",
            borderRadius: "50%",
            width: isMobile ? "45px" : "60px",
            height: isMobile ? "45px" : "60px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
            transition: "all 0.3s ease",
            zIndex: 10,
        },
    }

    const customSwiperStyles = `
    .swiper-pagination-bullet-custom {
      width: 12px;
      height: 12px;
      background: #d1d5db;
      border-radius: 50%;
      display: inline-block;
      margin: 0 6px;
      cursor: pointer;
      transition: all 0.3s ease;
    }
    .swiper-pagination-bullet-active-custom {
      background: #7fb3a3;
      transform: scale(1.3);
    }
    .swiper-button-prev-custom:hover,
    .swiper-button-next-custom:hover {
      background-color: #6a9b8e !important;
      transform: translateY(-50%) scale(1.1) !important;
    }
    
    @media (max-width: 768px) {
      .swiper-button-prev-custom,
      .swiper-button-next-custom {
        width: 45px !important;
        height: 45px !important;
      }
      .swiper-button-prev-custom {
        left: 10px !important;
      }
      .swiper-button-next-custom {
        right: 10px !important;
      }
    }
  `;

    return (
        <Layout
            activeMenuItem="Online Prayer Requests"
            breadcrumb={breadcrumb}
            sidebarCollapsed={sidebarCollapsed}
            setSidebarCollapsed={setSidebarCollapsed}
        >
            {/* Toast notifications will be shown via react-toastify */}

            <div style={styles.swiperContainer}>
                {isLoadingSlides ? (
                    <div style={{ padding: "40px", textAlign: "center", color: "#6B7280" }}>
                        Loading forms...
                    </div>
                ) : slides.length === 0 ? (
                    <div style={{ padding: "40px", textAlign: "center", color: "#6B7280" }}>
                        No forms available for this category.
                    </div>
                ) : (
                    <>
                        <Swiper
                            modules={[Navigation]}
                            spaceBetween={50}
                            slidesPerView={1}
                            navigation={{
                                nextEl: ".swiper-button-next-custom",
                                prevEl: ".swiper-button-prev-custom",
                            }}
                            centeredSlides={true}
                            loop={slides.length > 1}
                            style={{ padding: "0 120px" }}
                        >
                            {slides.map((slide) => (
                                <SwiperSlide key={slide.id}>
                                    <div style={styles.slideContainer}>
                                        <div style={slide.cardType === 'regular' ? styles.regularCard : styles.specialCard}>
                                            <div style={styles.formPreviewSection}>
                                                <img
                                                    src={slide.image}
                                                    alt={slide.imageAlt}
                                                    style={slide.cardType === 'regular' ? styles.formPreviewImg : styles.formPreviewImgSpecial}
                                                />
                                            </div>
                                            <div style={slide.cardType === 'regular' ? styles.cardContent : styles.specialCardContent}>
                                                <h2 style={slide.cardType === 'regular' ? styles.cardTitle : styles.specialCardTitle}>
                                                    {slide.title}
                                                </h2>
                                                <p style={slide.cardType === 'regular' ? styles.cardPrice : styles.specialCardPrice}>
                                                    {slide.priceText.replace('{price}', currencyConfig.format(slide.price))}
                                                </p>
                                                <button 
                                                    style={slide.cardType === 'regular' ? styles.fillBtn : styles.specialFillBtn} 
                                                    onClick={() => navigate(slide.route)}
                                                >
                                                    {slide.buttonText}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </SwiperSlide>
                            ))}
                        </Swiper>

                        {slides.length > 1 && (
                            <>
                                <div className="swiper-button-prev-custom" style={styles.navButtonPrev}>
                                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                        <polyline points="15,18 9,12 15,6"></polyline>
                                    </svg>
                                </div>
                                <div className="swiper-button-next-custom" style={styles.navButtonNext}>
                                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                        <polyline points="9,18 15,12 9,6"></polyline>
                                    </svg>
                                </div>
                            </>
                        )}
                    </>
                )}
            </div>

        </Layout>
    )
}

export default HomaRequestPage